import React from 'react';
import { Switch } from '@parrot-software/design-system';

import type { FrontendPermissionId } from '@personnel/domain/types/PermissionsConst';
import type { IconName } from './DynamicIcon';
import { DynamicIcon } from './DynamicIcon';
import { PermissionDebugInfo } from './PermissionDebugInfo';
import { usePermissionsDebug } from '@personnel/presentation/hooks/usePermissionsDebug';

/**
 * Permission Switch Component
 *
 * Renders a switch control for a permission.
 * Used for permissions that enable/disable features or control child permissions.
 *
 * @param {PermissionSwitchProps} props - Component properties
 * @returns {React.ReactElement} Permission switch component
 *
 * @example
 * // Simple switch
 * <PermissionSwitch
 *   permissionId="store-pos-access"
 *   label="Acceso al POS"
 *   description="Permite acceder al sistema POS"
 *   backendIds={['pos.pinAccess', 'payTerminal.terminalAccess']}
 *   checked={true}
 *   onChange={(checked) => console.log(checked)}
 * />
 *
 * @example
 * // Switch with icon (used in modules)
 * <PermissionSwitch
 *   permissionId="store-pos-cash-module"
 *   label="Módulo de Cajas"
 *   description="Permite acceder al módulo de cajas"
 *   backendIds={['areaTable.canViewCashFlow']}
 *   iconName="circle-dollar-sign"
 *   checked={true}
 *   onChange={(checked) => console.log(checked)}
 *   size="small"
 * />
 */

interface PermissionSwitchProps {
  /** Permission identifier */
  permissionId: FrontendPermissionId;
  /** Permission label */
  label: string;
  /** Optional description */
  description?: string;
  /** Backend permission identifiers */
  backendIds: readonly string[];
  /** Whether the switch is checked */
  checked: boolean;
  /** Callback when switch changes */
  onChange: (checked: boolean) => void;
  /** Whether the switch is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'default' | 'small';
  /** Icon name (optional) */
  iconName?: IconName;
}

const styles = {
  container: {
    default: 'flex items-start gap-3 py-2',
    small: 'flex items-start gap-2 py-1',
  },
  icon: 'size-5 text-foreground shrink-0',
  content: 'flex flex-col gap-1 flex-1',
  label: {
    default: 'text-base font-medium text-foreground cursor-pointer',
    small: 'text-sm font-medium text-foreground cursor-pointer',
  },
  description: {
    default: 'text-sm text-muted-foreground whitespace-pre-line',
    small: 'text-xs text-muted-foreground whitespace-pre-line',
  },
  switch: 'shrink-0',
};

export const PermissionSwitch = ({
  permissionId,
  label,
  description,
  backendIds,
  checked,
  onChange,
  disabled = false,
  size = 'default',
  iconName,
}: PermissionSwitchProps): React.ReactElement => {
  const { isDebugMode } = usePermissionsDebug();

  // Generate unique ID for label association
  const switchId = `switch-${permissionId}`;

  return (
    <div className={styles.container[size]}>
      {iconName && (
        <div className={styles.icon}>
          <DynamicIcon className="size-5 text-foreground" name={iconName} />
        </div>
      )}
      <div className={styles.content}>
        <label className={styles.label[size]} htmlFor={switchId}>
          {label}
        </label>

        {description && <p className={styles.description[size]}>{description}</p>}

        {/* Debug info - Only shown in development when enabled via console */}
        {isDebugMode && (
          <PermissionDebugInfo backendIds={backendIds} componentType="switch" frontendId={permissionId} />
        )}
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        id={switchId}
        onCheckedChange={(checked) => onChange(checked as boolean)}
      />
    </div>
  );
};
